import { useState, useCallback, useEffect, useRef } from 'react';
import { CATEGORIES, CATEGORY_TAX_MAP, INCOME_CATEGORIES, INCOME_CATEGORY_TAX_MAP, IRS_INCOME_CATEGORIES } from '../utils/constants';
import { loadCustomCategories, saveCustomCategories, loadCustomIncomeCategories, saveCustomIncomeCategories } from '../utils/firestoreStorage';
import { makeKey } from '../utils/categoryHelpers';

/**
 * Manages built-in + custom expense categories.
 *
 * - Authenticated users: custom categories are loaded from and saved to Firestore.
 * - Guests (uid == null): custom categories are not supported; addCategory is a no-op.
 */
export function useCategories(uid) {
  const [customCategories, setCustomCategories] = useState([]);
  const [customIncomeCategories, setCustomIncomeCategories] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const prevUid = useRef(uid);

  useEffect(() => {
    if (uid === prevUid.current && loaded) return;
    prevUid.current = uid;

    if (!uid) {
      setCustomCategories([]);
      setCustomIncomeCategories([]);
      setLoaded(true);
      return;
    }

    let cancelled = false;
    Promise.all([
      loadCustomCategories(uid),
      loadCustomIncomeCategories(uid),
    ]).then(([cats, incomeCats]) => {
      if (!cancelled) {
        setCustomCategories(Array.isArray(cats) ? cats : []);
        setCustomIncomeCategories(Array.isArray(incomeCats) ? incomeCats : []);
        setLoaded(true);
      }
    });
    return () => { cancelled = true; };
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const getTaxCategory = useCallback((categoryName, expenseTaxCategory) => {
    if (categoryName === 'other') return expenseTaxCategory || 'Uncategorized';
    if (CATEGORY_TAX_MAP[categoryName]) return CATEGORY_TAX_MAP[categoryName];
    const custom = customCategories.find(c => c.label === categoryName || c.name === categoryName);
    return custom?.taxCategory || 'Uncategorized';
  }, [customCategories]);

  const getIncomeTaxCategory = useCallback((categoryName, incomeTaxType) => {
    if (INCOME_CATEGORY_TAX_MAP[categoryName]) return INCOME_CATEGORY_TAX_MAP[categoryName];
    const custom = customIncomeCategories.find(c => c.label === categoryName || c.name === categoryName);
    return custom?.taxCategory || incomeTaxType || IRS_INCOME_CATEGORIES[IRS_INCOME_CATEGORIES.length - 1];
  }, [customIncomeCategories]);

  const addCategory = useCallback((name, taxCategory) => {
    if (!uid) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    setCustomCategories(prev => {
      if (prev.some(c => c.label === trimmed) || CATEGORIES.includes(trimmed)) return prev;
      const next = [...prev, { key: makeKey(trimmed), label: trimmed, taxCategory: taxCategory || 'Uncategorized' }];
      saveCustomCategories(uid, next).catch(err =>
        console.error('Failed to save custom categories:', err)
      );
      return next;
    });
  }, [uid]);

  const addIncomeCategory = useCallback((name, taxCategory) => {
    if (!uid) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    setCustomIncomeCategories(prev => {
      if (prev.some(c => c.label === trimmed) || INCOME_CATEGORIES.includes(trimmed)) return prev;
      const next = [...prev, { key: makeKey(trimmed), label: trimmed, taxCategory: taxCategory || 'Other Income' }];
      saveCustomIncomeCategories(uid, next).catch(err =>
        console.error('Failed to save custom income categories:', err)
      );
      return next;
    });
  }, [uid]);

  const builtInSet = new Set(CATEGORIES);
  const allCategories = [...CATEGORIES, ...customCategories.map(c => c.label ?? c.name).filter(l => !builtInSet.has(l))];
  const builtInIncomeSet = new Set(INCOME_CATEGORIES);
  const allIncomeCategories = [...INCOME_CATEGORIES, ...customIncomeCategories.map(c => c.label ?? c.name).filter(l => !builtInIncomeSet.has(l))];

  return {
    allCategories, customCategories, addCategory, getTaxCategory,
    allIncomeCategories, customIncomeCategories, addIncomeCategory, getIncomeTaxCategory,
    categoriesLoaded: loaded,
  };
}
